import Amplify from 'aws-amplify'
import { displayMessage } from '../helpers/helper.js'
import { i18n } from '@/i18n'
import { EventBus } from '@/event-bus'

export const OrderService = {
  /**
   * Sends a PATCH request to update an order with the provided data.
   *
   * If `patchData` is provided, this function constructs the request with the appropriate
   * authorization headers and sends it to the `/v1/int/orders/:orderId` endpoint using Amplify's API.
   * On success, it displays a success message and emits a `reLoadCounter` event.
   * On failure, it displays an error message based on the error type.
   *
   * @async
   * @function patch
   * @param {string} accessToken - Access token used for authenticating the API request.
   * @param {string|number} orderId - Unique identifier of the order to update.
   * @param {Object} [patchData=undefined] - Data to be sent in the PATCH request body.
   * @returns {Promise<Object|undefined>} - The response from the API if successful, otherwise `undefined`.
   *
   * @throws {Error} - Logs the error to the console and displays a user-friendly message.
   */
  async patch(accessToken, orderId, patchData = undefined) {
    if (patchData) {
      const path = '/v1/int/orders/' + orderId
      const myInit = {
        headers: {
          Authorization: 'Bearer ' + accessToken,
        },
        body: patchData,
      }

      try {
        const res = await Amplify.API.patch('orders', path, myInit)
        displayMessage(i18n.t('subOrder.orderUpdated'), 'success')
        EventBus.$emit('reLoadCounter')
        return res
      } catch (e) {
        const errorMessage = e?.response?.data?.message
        let errorText = i18n.t('order.errorUpdate')
        if (errorMessage === 'invalid_pickup_code') {
          errorText = i18n.t('order.invalidPickupCode')
        } else if (errorMessage === 'user_not_allowed_to_update_resource') {
          errorText = i18n.t('order.userNotAllowedToUpdate')
        }
        displayMessage(errorText)
        console.log(e)
      }
    }
  },

  /**
   * Retrieves the pickup code for a given order using the provided access token.
   *
   * @async
   * @function getPickupCode
   * @param {string} accessToken - The access token for API authentication.
   * @param {string|number} orderId - The unique identifier of the order.
   * @returns {Promise<string>} Resolves to the pickup code if successful.
   *
   * @throws {Error} Throws an error if the API call fails. The error object contains response details.
   *
   * @example
   * try {
   *   const code = await getPickupCode('yourAccessToken', '12345');
   *   console.log(code); // e.g., "ABC123"
   * } catch (error) {
   *   console.error(error.response?.data?.message); // e.g., "user_not_authorized"
   * }
   */
  async getPickupCode(accessToken, orderId) {
    const path = '/v1/int/orders/' + orderId + "/pickup-code"
    const myInit = {
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    }
    const pickupCodeResponse = await Amplify.API.get('orders', path, myInit)
    return pickupCodeResponse?.pickup_code
  },

  /**
   * Resend the pickup code for a given order using the provided access token.
   *
   * @async
   * @function resendPickupCode
   * @param {string} accessToken - The access token for API authentication.
   * @param {string|number} orderId - The unique identifier of the order.
   * @returns {Promise<void>} Displays a success message if the pickup code was resent successfully.
   *
   * @throws {Error} Throws an error if the API call fails. The error object contains response details.
   *
   * @example
   * try {
   *   await resendPickupCode('yourAccessToken', '12345');
   *   // Success message displayed automatically
   * } catch (error) {
   *   console.error(error.response?.data?.message); // e.g., "user_not_authorized"
   * }
   */
  async resendPickupCode(accessToken, orderId) {
    const path = '/v1/int/orders/' + orderId + '/resend-pickup-code'
    const myInit = {
      headers: {
        Authorization: 'Bearer ' + accessToken,
      },
    }
    await Amplify.API.post('orders', path, myInit)
    displayMessage(i18n.t('order.pickupCodeResentSuccessfully'), 'success')
  },
}
